import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { bildirimSesiCal, sesKilidiniAc, titret } from '../bildirimSesi';
import {
  urunGrupListesi, urunGrupOlustur, urunGrupGuncelle, urunGrupSil,
  urunListesi, urunOlustur, urunGuncelle, urunSil, gorselYukle, dosyaUrl,
  isletmeBilgilerimGetir, bannerYukle, logoYukle, ipGuncelle, temaGuncelle, wifiBilgiGuncelle, fonksiyonAyarlariGuncelle, googleYorumLinkiGuncelle,
  isletmeBilgileriGuncelle,
  siparisBekleyenListesi, siparisEskiListesi, siparisHazirlaniyor, siparisTeslimEdildi, siparisIptal,
  personelListesi, personelOlustur, personelAktifDegistir, personelSil,
  personelMasaAtamalariGetir, personelMasaAtamasiGuncelle,
  istatistikGetir,
  masaListesi, masaOlustur, masaGuncelle, masaSil,
  bildirimListesi, bildirimOnayla, adisyonGetir, adisyonKapat,
  pushPublicKeyGetir, pushAboneOl
} from '../services/api';
import { TEMALAR } from '../temalar';
import { paraFormatla } from '../paraFormat';

// Sekme çubuğundaki her butona ayrı bir renk vermek için — sekmeler birbirinden görsel olarak
// ayrışsın diye (kullanıcı talebi: 2026-07-27, "rengarenk butonlar").
const SEKME_RENKLERI = ['#e8600c', '#2e7d32', '#1565c0', '#8e24aa', '#c62828', '#00838f', '#6d4c41', '#ad1457', '#37474f'];

// Push aboneliği için VAPID public key'i tarayıcının beklediği Uint8Array formatına çevirir.
function urlBase64ToUint8Array(base64String) {
  const dolgu = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + dolgu).replace(/-/g, '+').replace(/_/g, '/');
  const ham = window.atob(base64);
  return Uint8Array.from([...ham].map((c) => c.charCodeAt(0)));
}

export default function IsletmePanel() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const rol = localStorage.getItem('isletmeRol') || 'MUDUR';

  const [sekme, setSekme] = useState(rol === 'GARSON' ? 'SIPARISLER' : 'ISLETME_BILGILERI');
  const [bildirimIzniDurumu, setBildirimIzniDurumu] = useState(''); // '', 'acik', 'reddedildi', 'desteklenmiyor'
  const [bildirimHataMetni, setBildirimHataMetni] = useState('');

  const bildirimleriAc = async () => {
    setBildirimHataMetni('');
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setBildirimIzniDurumu('desteklenmiyor');
      return;
    }
    try {
      const izin = await Notification.requestPermission();
      if (izin !== 'granted') { setBildirimIzniDurumu('reddedildi'); setBildirimHataMetni('İzin reddedildi.'); return; }

      const kayit = await navigator.serviceWorker.ready;

      // Daha önce (farklı bir anahtarla) abone olunmuşsa önce onu iptal et — yoksa tarayıcı
      // "zaten farklı bir anahtarla abonesin" diye yeni aboneliği reddeder.
      const eskiAbonelik = await kayit.pushManager.getSubscription();
      if (eskiAbonelik) await eskiAbonelik.unsubscribe();

      const anahtarRes = await pushPublicKeyGetir();
      if (!anahtarRes.data?.publicKey) throw new Error('Sunucudan VAPID anahtarı alınamadı.');

      const abonelik = await kayit.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(anahtarRes.data.publicKey)
      });
      const veri = abonelik.toJSON();
      await pushAboneOl(veri.endpoint, veri.keys.p256dh, veri.keys.auth);
      setBildirimIzniDurumu('acik');
    } catch (err) {
      setBildirimIzniDurumu('reddedildi');
      setBildirimHataMetni(err.response?.data?.hata || err.message || 'Bilinmeyen hata.');
    }
  };

  // Panel her açıldığında kullanıcıya "Bildirimleri Aç" butonuna tekrar bastırmamak için: tarayıcı
  // izni zaten verilmiş VE bir push aboneliği kayıtlıysa, ekran kendisi doğrulayıp yeşile döner.
  // Ayrıca sunucudaki PUSH_ABONELIK kaydını (garsonId dahil) her panel açılışında TAZELER — bu, masa
  // ataması özelliğinden ÖNCE oluşturulmuş eski aboneliklerin GARSON_ID'sinin sonsuza kadar NULL (yani
  // "kısıtsız/müdür gibi") kalmasını önler; kullanıcı tekrar "izinleri sıfırla" yapmak zorunda kalmaz.
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    navigator.serviceWorker.ready
      .then((kayit) => kayit.pushManager.getSubscription())
      .then((abonelik) => {
        if (!abonelik) return;
        setBildirimIzniDurumu('acik');
        const veri = abonelik.toJSON();
        pushAboneOl(veri.endpoint, veri.keys.p256dh, veri.keys.auth).catch(() => {});
      })
      .catch(() => {});
  }, []);

  // Yeni sipariş/bildirim geldiğinde ses+titreşimle haber vermek için: her yenilemede gelen id'leri
  // bir öncekiyle kıyaslıyoruz. İlk yüklemede (ref henüz boşken) ses ÇALINMAZ — yalnızca panel zaten
  // açıkken YENİ bir kayıt gelirse çalar.
  const bilinenSiparisIdleriRef = useRef(null);
  const bilinenBildirimIdleriRef = useRef(null);
  const yeniVarMiKontrolEt = (ref, liste) => {
    const yeniIdSeti = new Set(liste.map((x) => x.id));
    if (ref.current !== null) {
      const yeniEklenenVar = [...yeniIdSeti].some((id) => !ref.current.has(id));
      if (yeniEklenenVar) {
        bildirimSesiCal();
        titret();
      }
    }
    ref.current = yeniIdSeti;
  };

  const [gruplar, setGruplar] = useState([]);
  const [urunler, setUrunler] = useState([]);
  const [hata, setHata] = useState('');
  const [yukleniyor, setYukleniyor] = useState(true);

  const [bannerUrl, setBannerUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [bannerYukleniyor, setBannerYukleniyor] = useState(false);
  const [logoYukleniyor, setLogoYukleniyor] = useState(false);
  const [bitisTarihi, setBitisTarihi] = useState(null);
  const [kalanGun, setKalanGun] = useState(null);
  const [izinVerilenIp, setIzinVerilenIp] = useState('');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiSifre, setWifiSifre] = useState('');
  const [wifiBilgiKaydediliyor, setWifiBilgiKaydediliyor] = useState(false);
  const [wifiBilgiMesaj, setWifiBilgiMesaj] = useState('');

  const [googleYorumLink, setGoogleYorumLink] = useState('');
  const [googleYorumKaydediliyor, setGoogleYorumKaydediliyor] = useState(false);
  const [googleYorumMesaj, setGoogleYorumMesaj] = useState('');

  const googleYorumLinkiKaydet = (e) => {
    e.preventDefault();
    setGoogleYorumKaydediliyor(true); setGoogleYorumMesaj('');
    googleYorumLinkiGuncelle(googleYorumLink)
      .then(() => setGoogleYorumMesaj({ tip: 'basari', metin: 'Kaydedildi.' }))
      .catch((err) => setGoogleYorumMesaj({ tip: 'hata', metin: err.response?.data?.hata || 'Kaydedilemedi.' }))
      .finally(() => setGoogleYorumKaydediliyor(false));
  };

  const [isletmeBilgiAd, setIsletmeBilgiAd] = useState('');
  const [isletmeBilgiTelefon, setIsletmeBilgiTelefon] = useState('');
  const [isletmeBilgiAdres, setIsletmeBilgiAdres] = useState('');
  const [isletmeBilgiEkBilgi, setIsletmeBilgiEkBilgi] = useState('');
  const [calismaSaatHaftaIci, setCalismaSaatHaftaIci] = useState('');
  const [calismaSaatCumartesi, setCalismaSaatCumartesi] = useState('');
  const [calismaSaatPazar, setCalismaSaatPazar] = useState('');
  const [isletmeBilgiKaydediliyor, setIsletmeBilgiKaydediliyor] = useState(false);
  const [isletmeBilgiMesaj, setIsletmeBilgiMesaj] = useState(null);

  const isletmeBilgileriKaydet = (e) => {
    e.preventDefault();
    setIsletmeBilgiKaydediliyor(true); setIsletmeBilgiMesaj(null);
    isletmeBilgileriGuncelle({
      ad: isletmeBilgiAd,
      telefon: isletmeBilgiTelefon,
      adres: isletmeBilgiAdres,
      ekBilgi: isletmeBilgiEkBilgi,
      calismaSaatHaftaIci,
      calismaSaatCumartesi,
      calismaSaatPazar
    })
      .then(() => {
        localStorage.setItem('isletmeAd', isletmeBilgiAd);
        setIsletmeBilgiMesaj({ tip: 'basari', metin: 'Kaydedildi.' });
      })
      .catch((err) => setIsletmeBilgiMesaj({ tip: 'hata', metin: err.response?.data?.hata || 'Kaydedilemedi.' }))
      .finally(() => setIsletmeBilgiKaydediliyor(false));
  };

  const wifiBilgiKaydet = (e) => {
    e.preventDefault();
    setWifiBilgiKaydediliyor(true);
    setWifiBilgiMesaj('');
    wifiBilgiGuncelle(wifiSsid, wifiSifre)
      .then(() => setWifiBilgiMesaj('Kaydedildi.'))
      .catch(() => setWifiBilgiMesaj('Kaydedilemedi.'))
      .finally(() => setWifiBilgiKaydediliyor(false));
  };
  const [siparisAyar, setSiparisAyar] = useState('ZORUNLU');
  const [garsonCagirAyar, setGarsonCagirAyar] = useState('ZORUNLU');
  const [hesapIsteAyar, setHesapIsteAyar] = useState('ZORUNLU');
  const [fonksiyonKaydediliyor, setFonksiyonKaydediliyor] = useState(false);
  const [fonksiyonMesaj, setFonksiyonMesaj] = useState('');

  const fonksiyonAyarlariKaydet = () => {
    setFonksiyonKaydediliyor(true);
    setFonksiyonMesaj('');
    fonksiyonAyarlariGuncelle(siparisAyar, garsonCagirAyar, hesapIsteAyar)
      .then(() => setFonksiyonMesaj('Kaydedildi.'))
      .catch(() => setFonksiyonMesaj('Kaydedilemedi.'))
      .finally(() => setFonksiyonKaydediliyor(false));
  };

  const [tema, setTema] = useState('turuncu');
  const [temaKaydediliyor, setTemaKaydediliyor] = useState(false);

  const temaSec = (id) => {
    setTemaKaydediliyor(true);
    temaGuncelle(id)
      .then(() => setTema(id))
      .catch(() => {})
      .finally(() => setTemaKaydediliyor(false));
  };
  const [ipGuncelleniyor, setIpGuncelleniyor] = useState(false);
  const [ipMesaj, setIpMesaj] = useState('');

  const [bildirimler, setBildirimler] = useState([]);
  const [bildirimlerYukleniyor, setBildirimlerYukleniyor] = useState(true);

  const bildirimleriYenile = () => {
    setBildirimlerYukleniyor(true);
    bildirimListesi()
      .then((res) => {
        yeniVarMiKontrolEt(bilinenBildirimIdleriRef, res.data);
        setBildirimler(res.data);
      })
      .catch(() => {})
      .finally(() => setBildirimlerYukleniyor(false));
  };

  const bildirimiOnayla = (id) => {
    bildirimOnayla(id).then(bildirimleriYenile).catch(() => {});
  };

  const [adisyonMasaNo, setAdisyonMasaNo] = useState('');
  const [adisyonKayitlar, setAdisyonKayitlar] = useState(null);
  const [adisyonToplam, setAdisyonToplam] = useState(0);
  const [adisyonYukleniyor, setAdisyonYukleniyor] = useState(false);
  const [adisyonKapatPopup, setAdisyonKapatPopup] = useState(false);

  const adisyonuGetir = (e) => {
    e.preventDefault();
    if (!adisyonMasaNo) return;
    setAdisyonYukleniyor(true);
    adisyonGetir(adisyonMasaNo)
      .then((res) => { setAdisyonKayitlar(res.data); setAdisyonToplam(res.data.reduce((t, g) => t + g.toplam, 0)); })
      .catch(() => {})
      .finally(() => setAdisyonYukleniyor(false));
  };

  const adisyonuKapat = () => {
    adisyonKapat(adisyonMasaNo)
      .then(() => { setAdisyonKayitlar(null); setAdisyonToplam(0); setAdisyonKapatPopup(false); })
      .catch(() => {});
  };

  const [siparisSekme, setSiparisSekme] = useState('BEKLEYEN'); // BEKLEYEN | ESKI
  const [bekleyenSiparisler, setBekleyenSiparisler] = useState([]);
  const [eskiSiparisler, setEskiSiparisler] = useState([]);
  const [siparisYukleniyor, setSiparisYukleniyor] = useState(true);

  // "Eski Siparişler" temizleme, cihaz+rol bazında kalıcıdır (localStorage) — sayfa yenilense de
  // temizlenen kayıtlar geri gelmez. Müdür ve garson ayrı anahtar kullanır, biri temizlese diğeri
  // etkilenmez (örn. garson kendi ekranını temizler, müdür geçmişi görmeye devam eder).
  const eskiTemizlemeAnahtari = `eskiSiparisTemizlemeTarihi_${localStorage.getItem('isletmeId')}_${rol}`;
  const eskiSiparisleriFiltrele = (liste) => {
    const sinir = localStorage.getItem(eskiTemizlemeAnahtari);
    if (!sinir) return liste;
    return liste.filter((g) => new Date(g.tarih) > new Date(sinir));
  };
  const eskiListesiniTemizle = () => {
    localStorage.setItem(eskiTemizlemeAnahtari, new Date().toISOString());
    setEskiSiparisler([]);
  };

  const [masalar, setMasalar] = useState([]);
  const [masalarYukleniyor, setMasalarYukleniyor] = useState(true);
  const [yeniMasaAd, setYeniMasaAd] = useState('');
  const [masaHata, setMasaHata] = useState('');
  const [masaKaydediliyor, setMasaKaydediliyor] = useState(false);
  const [masaDuzenlenenId, setMasaDuzenlenenId] = useState(null);
  const [masaDuzenlenenAd, setMasaDuzenlenenAd] = useState('');

  const masalariYenile = () => {
    setMasalarYukleniyor(true);
    masaListesi()
      .then((res) => setMasalar(res.data))
      .catch(() => {})
      .finally(() => setMasalarYukleniyor(false));
  };

  const masaEkle = (e) => {
    e.preventDefault();
    setMasaHata(''); setMasaKaydediliyor(true);
    masaOlustur(yeniMasaAd)
      .then(() => { setYeniMasaAd(''); masalariYenile(); })
      .catch((err) => setMasaHata(err.response?.data?.hata || 'Kaydedilemedi.'))
      .finally(() => setMasaKaydediliyor(false));
  };

  const masaDuzenlemeyeBasla = (m) => { setMasaDuzenlenenId(m.id); setMasaDuzenlenenAd(m.ad); };

  const masaDuzenlemeyiKaydet = (id) => {
    masaGuncelle(id, masaDuzenlenenAd)
      .then(() => { setMasaDuzenlenenId(null); masalariYenile(); })
      .catch((err) => setMasaHata(err.response?.data?.hata || 'Kaydedilemedi.'));
  };

  const masaSilOnayla = (m) => {
    if (!window.confirm(`"${m.ad}" masasını silmek istediğinize emin misiniz?`)) return;
    masaSil(m.id).then(masalariYenile).catch(() => {});
  };

  const masaQrOlustur = async (m) => {
    // window.open, kullanıcı tıklamasının (gesture) hemen ardından SENKRON çağrılmalı — yoksa
    // await sonrası çağrılırsa tarayıcılar bunu popup engelleyici ile bloklar.
    const pencere = window.open('', '_blank');
    const link = `${window.location.origin}${process.env.PUBLIC_URL}/${slug}?masa=${encodeURIComponent(m.ad)}`;
    const dataUrl = await QRCode.toDataURL(link, { width: 500, margin: 2 });
    if (!pencere) return;
    pencere.document.write(`
      <!DOCTYPE html>
      <html lang="tr">
      <head>
        <meta charset="utf-8" />
        <title>Masa ${m.ad} — QR Kod</title>
        <style>
          @media print {
            @page { margin: 0; }
            body * { visibility: hidden; }
            .yazdir-alani, .yazdir-alani * { visibility: visible; }
            .yazdir-alani {
              position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
              margin: 0; padding: 0;
            }
            .ekran-only { display: none !important; }
          }
        </style>
      </head>
      <body style="margin:0;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:Arial,sans-serif;padding:40px;box-sizing:border-box;">
        <h2 class="ekran-only" style="color:#1e2a3a;margin:0 0 20px;">Masa ${m.ad}</h2>
        <div class="yazdir-alani">
          <img src="${dataUrl}" alt="Masa ${m.ad} QR Kodu" style="width:400px;height:400px;display:block;" />
        </div>
        <p class="ekran-only" style="font-size:13px;color:#666;word-break:break-all;max-width:400px;margin:20px auto 0;text-align:center;">${link}</p>
        <div class="ekran-only" style="margin-top:20px;">
          <button onclick="window.print()" style="padding:12px 28px;font-size:16px;font-weight:bold;cursor:pointer;background:#e8600c;color:#fff;border:none;border-radius:6px;">Yazdır</button>
        </div>
      </body>
      </html>
    `);
    pencere.document.close();
  };

  const [istBaslangic, setIstBaslangic] = useState('');
  const [istBitis, setIstBitis] = useState('');
  const [istMasaNo, setIstMasaNo] = useState('');
  const [istGrupId, setIstGrupId] = useState('');
  const [istUrunId, setIstUrunId] = useState('');
  const [istDurum, setIstDurum] = useState(''); // '' = Tümü, 'ONAYLI' = iptal hariç, 'IPTAL' = yalnızca iptal
  const [istKayitlar, setIstKayitlar] = useState(null);
  const [istToplam, setIstToplam] = useState(0);
  const [istYukleniyor, setIstYukleniyor] = useState(false);

  const istatistikFiltrele = (e) => {
    e.preventDefault();
    setIstYukleniyor(true);
    istatistikGetir({
      baslangic: istBaslangic || undefined,
      bitis: istBitis || undefined,
      masaNo: istMasaNo || undefined,
      grupId: istUrunId ? undefined : (istGrupId || undefined),
      urunId: istUrunId || undefined,
      durum: istDurum || undefined
    })
      .then((res) => { setIstKayitlar(res.data.kayitlar); setIstToplam(res.data.toplam); })
      .catch(() => {})
      .finally(() => setIstYukleniyor(false));
  };

  const [personel, setPersonel] = useState([]);
  const [personelYukleniyor, setPersonelYukleniyor] = useState(true);
  const [yeniPersonelKullaniciAdi, setYeniPersonelKullaniciAdi] = useState('');
  const [yeniPersonelSifre, setYeniPersonelSifre] = useState('');
  const [personelHata, setPersonelHata] = useState('');
  const [personelKaydediliyor, setPersonelKaydediliyor] = useState(false);

  const [grupModalAcik, setGrupModalAcik] = useState(false);
  const [grupDuzenlenen, setGrupDuzenlenen] = useState(null); // null = yeni, obje = düzenleme
  const [grupAd, setGrupAd] = useState('');
  const [grupSira, setGrupSira] = useState(0);

  const [urunModalAcik, setUrunModalAcik] = useState(false);
  const [urunDuzenlenen, setUrunDuzenlenen] = useState(null);
  const [urunGrupId, setUrunGrupId] = useState('');
  const [urunAd, setUrunAd] = useState('');
  const [urunFiyat, setUrunFiyat] = useState('');
  const [urunAciklama, setUrunAciklama] = useState('');
  const [urunGorselUrl, setUrunGorselUrl] = useState('');
  const [gorselYukleniyor, setGorselYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [modalHata, setModalHata] = useState('');

  const listeyiYenile = () => {
    setYukleniyor(true);
    Promise.all([urunGrupListesi(), urunListesi()])
      .then(([grupRes, urunRes]) => { setGruplar(grupRes.data); setUrunler(urunRes.data); })
      .catch((err) => setHata(err.response?.data?.hata || 'Veriler alınamadı.'))
      .finally(() => setYukleniyor(false));
  };

  useEffect(() => {
    if (!localStorage.getItem('isletmeId') || localStorage.getItem('isletmeSlug') !== slug) {
      navigate(`/${slug}/ayarlar`);
      return;
    }
    siparisleriYenile();
    bildirimleriYenile();
    masalariYenile();
    if (rol !== 'GARSON') {
      listeyiYenile();
      isletmeBilgilerimGetir().then((res) => {
        setBannerUrl(res.data.bannerUrl || '');
        setLogoUrl(res.data.logoUrl || '');
        setBitisTarihi(res.data.bitisTarihi);
        setKalanGun(res.data.kalanGun);
        setIzinVerilenIp(res.data.izinVerilenIp || '');
        setWifiSsid(res.data.wifiSsid || '');
        setWifiSifre(res.data.wifiSifre || '');
        setSiparisAyar(res.data.siparisAyar || 'ZORUNLU');
        setGarsonCagirAyar(res.data.garsonCagirAyar || 'ZORUNLU');
        setHesapIsteAyar(res.data.hesapIsteAyar || 'ZORUNLU');
        setTema(res.data.tema || 'turuncu');
        setGoogleYorumLink(res.data.googleYorumLink || '');
        setIsletmeBilgiAd(res.data.ad || '');
        setIsletmeBilgiTelefon(res.data.telefon || '');
        setIsletmeBilgiAdres(res.data.adres || '');
        setIsletmeBilgiEkBilgi(res.data.ekBilgi || '');
        setCalismaSaatHaftaIci(res.data.calismaSaatHaftaIci || '');
        setCalismaSaatCumartesi(res.data.calismaSaatCumartesi || '');
        setCalismaSaatPazar(res.data.calismaSaatPazar || '');
      }).catch(() => {});
      personelleriYenile();
    }

    // Yeni sipariş/bildirim geldiğinde ses çalabilmek için panel açıkken periyodik olarak yenile.
    const zamanlayici = setInterval(() => {
      siparisleriYenile();
      bildirimleriYenile();
    }, 12000);
    return () => clearInterval(zamanlayici);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const siparisleriYenile = () => {
    setSiparisYukleniyor(true);
    Promise.all([siparisBekleyenListesi(), siparisEskiListesi()])
      .then(([bekleyenRes, eskiRes]) => {
        yeniVarMiKontrolEt(bilinenSiparisIdleriRef, bekleyenRes.data);
        setBekleyenSiparisler(bekleyenRes.data);
        setEskiSiparisler(eskiSiparisleriFiltrele(eskiRes.data));
      })
      .catch(() => {})
      .finally(() => setSiparisYukleniyor(false));
  };

  const [onayPopup, setOnayPopup] = useState(null); // { siparis, aksiyon: 'HAZIRLANIYOR' | 'TESLIM_EDILDI' }

  const onayPopupOnayla = () => {
    const istek = onayPopup.aksiyon === 'HAZIRLANIYOR'
      ? siparisHazirlaniyor(onayPopup.siparis.id)
      : siparisTeslimEdildi(onayPopup.siparis.id);
    istek.then(siparisleriYenile).catch(() => {});
    setOnayPopup(null);
  };

  const siparisiIptalEt = (id) => {
    if (!window.confirm('Bu siparişi iptal etmek istediğinize emin misiniz?')) return;
    siparisIptal(id).then(siparisleriYenile).catch(() => {});
  };

  const [garsonMasaAtamalari, setGarsonMasaAtamalari] = useState({}); // { garsonId: [masaId,...] } — yoksa Tümü
  const [masaAtamaAcikId, setMasaAtamaAcikId] = useState(null); // hangi garsonun masa atama paneli açık
  const [masaAtamaKaydediliyor, setMasaAtamaKaydediliyor] = useState(false);

  const personelleriYenile = () => {
    setPersonelYukleniyor(true);
    Promise.all([personelListesi(), personelMasaAtamalariGetir()])
      .then(([pRes, aRes]) => {
        setPersonel(pRes.data);
        const map = {};
        aRes.data.forEach((a) => { map[a.garsonId] = a.masaIdleri; });
        setGarsonMasaAtamalari(map);
      })
      .catch(() => {})
      .finally(() => setPersonelYukleniyor(false));
  };

  // Bir garsonun belirli bir masaya kısıtlı mı yoksa "Tümü" mü (varsayılan, kısıtsız) olduğunu döner.
  const garsonMasalari = (garsonId) => garsonMasaAtamalari[garsonId] || [];

  const masaAtamasiKaydet = (garsonId, masaIdleri) => {
    setMasaAtamaKaydediliyor(true);
    personelMasaAtamasiGuncelle(garsonId, masaIdleri)
      .then(() => setGarsonMasaAtamalari((mevcut) => ({ ...mevcut, [garsonId]: masaIdleri })))
      .catch(() => {})
      .finally(() => setMasaAtamaKaydediliyor(false));
  };

  const masaSecimiToggle = (garsonId, masaId) => {
    const mevcutListe = garsonMasalari(garsonId);
    const yeniListe = mevcutListe.includes(masaId)
      ? mevcutListe.filter((id) => id !== masaId)
      : [...mevcutListe, masaId];
    masaAtamasiKaydet(garsonId, yeniListe);
  };

  const personelEkle = (e) => {
    e.preventDefault();
    setPersonelHata(''); setPersonelKaydediliyor(true);
    personelOlustur({ kullaniciAdi: yeniPersonelKullaniciAdi, sifre: yeniPersonelSifre })
      .then(() => {
        setYeniPersonelKullaniciAdi(''); setYeniPersonelSifre('');
        personelleriYenile();
      })
      .catch((err) => setPersonelHata(err.response?.data?.hata || 'Kaydedilemedi.'))
      .finally(() => setPersonelKaydediliyor(false));
  };

  const personelAktifTikla = (g) => {
    personelAktifDegistir(g.id, !g.aktif).then(personelleriYenile).catch(() => {});
  };

  const personelSilOnayla = (g) => {
    if (!window.confirm(`"${g.kullaniciAdi}" garson hesabını silmek istediğinize emin misiniz?`)) return;
    personelSil(g.id).then(personelleriYenile).catch(() => {});
  };

  const ipButonaBasildi = () => {
    setIpMesaj(''); setIpGuncelleniyor(true);
    ipGuncelle()
      .then((res) => { setIzinVerilenIp(res.data.ip); setIpMesaj(`Kaydedildi: ${res.data.ip}`); })
      .catch((err) => setIpMesaj(err.response?.data?.hata || 'IP kaydedilemedi.'))
      .finally(() => setIpGuncelleniyor(false));
  };

  const bannerSecildi = (e) => {
    const dosya = e.target.files[0];
    if (!dosya) return;
    setBannerYukleniyor(true);
    bannerYukle(dosya)
      .then((res) => setBannerUrl(res.data.url))
      .catch((err) => setHata(err.response?.data?.hata || 'Banner yüklenemedi.'))
      .finally(() => setBannerYukleniyor(false));
  };

  const logoSecildi = (e) => {
    const dosya = e.target.files[0];
    if (!dosya) return;
    setLogoYukleniyor(true);
    logoYukle(dosya)
      .then((res) => setLogoUrl(res.data.url))
      .catch((err) => setHata(err.response?.data?.hata || 'Logo yüklenemedi.'))
      .finally(() => setLogoYukleniyor(false));
  };

  const cikisYap = () => {
    localStorage.removeItem('isletmeId');
    localStorage.removeItem('isletmeAd');
    localStorage.removeItem('isletmeSlug');
    localStorage.removeItem('isletmeRol');
    localStorage.removeItem('garsonId');
    localStorage.removeItem('garsonAdi');
    navigate(`/${slug}`);
  };

  // --- Ürün Grubu ---
  const grupModaliAc = (grup) => {
    setGrupDuzenlenen(grup || null);
    setGrupAd(grup ? grup.ad : '');
    setGrupSira(grup ? grup.sira : gruplar.length);
    setModalHata('');
    setGrupModalAcik(true);
  };

  const grupKaydet = (e) => {
    e.preventDefault();
    setModalHata(''); setKaydediliyor(true);
    const dto = { ad: grupAd, sira: Number(grupSira) };
    const istek = grupDuzenlenen ? urunGrupGuncelle(grupDuzenlenen.id, dto) : urunGrupOlustur(dto);
    istek
      .then(() => { setGrupModalAcik(false); listeyiYenile(); })
      .catch((err) => setModalHata(err.response?.data?.hata || 'Kaydedilemedi.'))
      .finally(() => setKaydediliyor(false));
  };

  const grupSilOnayla = (grup) => {
    if (!window.confirm(`"${grup.ad}" grubunu ve içindeki tüm ürünleri kaldırmak istediğinize emin misiniz?`)) return;
    urunGrupSil(grup.id).then(listeyiYenile).catch((err) => setHata(err.response?.data?.hata || 'Silinemedi.'));
  };

  // --- Ürün ---
  // urun: düzenlenen GERÇEK ürün kaydı (id'si vardır) — yalnızca bu durumda "düzenleme modu".
  // varsayilanGrupId: bir grubun "+ Ürün Ekle" butonundan açılırken o grubu önceden seçili getirmek için.
  const urunModaliAc = (urun, varsayilanGrupId) => {
    const duzenlemeModu = !!(urun && urun.id);
    setUrunDuzenlenen(duzenlemeModu ? urun : null);
    setUrunGrupId(duzenlemeModu ? urun.grupId : (varsayilanGrupId || gruplar[0]?.id || ''));
    setUrunAd(duzenlemeModu ? urun.ad : '');
    setUrunFiyat(duzenlemeModu ? urun.fiyat : '');
    setUrunAciklama(duzenlemeModu ? urun.aciklama || '' : '');
    setUrunGorselUrl(duzenlemeModu ? urun.gorselUrl || '' : '');
    setModalHata('');
    setUrunModalAcik(true);
  };

  const gorselSecildi = (e) => {
    const dosya = e.target.files[0];
    if (!dosya) return;
    setGorselYukleniyor(true);
    gorselYukle(dosya)
      .then((res) => setUrunGorselUrl(res.data.url))
      .catch((err) => setModalHata(err.response?.data?.hata || 'Görsel yüklenemedi.'))
      .finally(() => setGorselYukleniyor(false));
  };

  const urunKaydet = (e) => {
    e.preventDefault();
    if (!urunGrupId) { setModalHata('Lütfen bir ürün grubu seçiniz.'); return; }
    setModalHata(''); setKaydediliyor(true);
    const dto = {
      grupId: Number(urunGrupId),
      ad: urunAd,
      fiyat: Number(urunFiyat) || 0,
      aciklama: urunAciklama,
      gorselUrl: urunGorselUrl,
      sira: urunDuzenlenen ? urunDuzenlenen.sira : urunler.length
    };
    const istek = urunDuzenlenen ? urunGuncelle(urunDuzenlenen.id, dto) : urunOlustur(dto);
    istek
      .then(() => { setUrunModalAcik(false); listeyiYenile(); })
      .catch((err) => setModalHata(err.response?.data?.hata || 'Kaydedilemedi.'))
      .finally(() => setKaydediliyor(false));
  };

  const urunSilOnayla = (urun) => {
    if (!window.confirm(`"${urun.ad}" ürününü kaldırmak istediğinize emin misiniz?`)) return;
    urunSil(urun.id).then(listeyiYenile).catch((err) => setHata(err.response?.data?.hata || 'Silinemedi.'));
  };

  const bildirimSekmeAdi = `Bildirimler${bildirimler.length > 0 ? ` (${bildirimler.length})` : ''}`;

  const sekmeler = rol === 'GARSON'
    ? [
        { id: 'SIPARISLER', ad: 'Siparişler' },
        { id: 'BILDIRIMLER', ad: bildirimSekmeAdi },
        { id: 'ADISYON', ad: 'Adisyon' }
      ]
    : [
        { id: 'ISLETME_BILGILERI', ad: 'İşletme Bilgileri' },
        { id: 'MENU', ad: 'Menü Tasarımı' },
        { id: 'WIFI', ad: 'Wifi Ayarı' },
        { id: 'PERSONEL', ad: 'Personel' },
        { id: 'MASALAR', ad: 'Masalar' },
        { id: 'SIPARISLER', ad: 'Siparişler' },
        { id: 'BILDIRIMLER', ad: bildirimSekmeAdi },
        { id: 'ADISYON', ad: 'Adisyon' },
        { id: 'ISTATISTIK', ad: 'İstatistikler' }
      ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }} onClick={sesKilidiniAc}>
      {rol === 'GARSON' ? (
        <div style={{ position: 'relative', marginBottom: 14, paddingTop: 6 }}>
          <div style={{ position: 'absolute', top: 0, left: 0 }}>
            {bildirimIzniDurumu !== 'acik' ? (
              <button onClick={bildirimleriAc} className="btn btn-vurgu" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                🔔 Bildirimleri Aç
              </button>
            ) : (
              <span style={{ fontSize: 12, fontWeight: 700, color: '#1e5c30', whiteSpace: 'nowrap' }}>🔔 Bildirimler Açık</span>
            )}
          </div>
          <div style={{ position: 'absolute', top: 0, right: 0 }}>
            <button onClick={cikisYap} className="btn btn-kirmizi" style={{ whiteSpace: 'nowrap' }}>Çıkış</button>
          </div>
          <div style={{ textAlign: 'center', paddingTop: 38 }}>
            <h1 style={{ margin: 0, fontSize: 24, color: '#1e2a3a' }}>{localStorage.getItem('isletmeAd')}</h1>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#6b7684', marginTop: 2 }}>Garson Paneli</div>
            <div style={{ fontSize: 12, color: '#9aa1a9', marginTop: 2 }}>({localStorage.getItem('garsonAdi')})</div>
          </div>
        </div>
      ) : (
        <div style={{ position: 'relative', marginBottom: 14, paddingTop: 6 }}>
          <div style={{ position: 'absolute', top: 0, left: 0 }}>
            {bildirimIzniDurumu !== 'acik' ? (
              <button onClick={bildirimleriAc} className="btn btn-vurgu" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                🔔 Bildirimleri Aç
              </button>
            ) : (
              <span style={{ fontSize: 12, fontWeight: 700, color: '#1e5c30', whiteSpace: 'nowrap' }}>🔔 Bildirimler Açık</span>
            )}
          </div>
          <div style={{ position: 'absolute', top: 0, right: 0 }}>
            <button onClick={cikisYap} className="btn btn-kirmizi" style={{ whiteSpace: 'nowrap' }}>Çıkış</button>
          </div>
          <div style={{ textAlign: 'center', paddingTop: 38 }}>
            <h1 style={{ margin: 0, fontSize: 24, color: '#1e2a3a' }}>{localStorage.getItem('isletmeAd')}</h1>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#6b7684', marginTop: 2 }}>Yönetim Paneli</div>
            {bitisTarihi && (
              <div style={{ fontSize: 13, color: kalanGun <= 30 ? '#c0392b' : '#9aa1a9', fontWeight: 600, marginTop: 4 }}>
                Lisans Bitiş Tarihi: {new Date(bitisTarihi).toLocaleDateString('tr-TR')} ({kalanGun} gün kaldı)
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <select
                value=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  window.open(`${process.env.PUBLIC_URL}/${slug}?masa=${encodeURIComponent(e.target.value)}`, '_blank');
                  e.target.value = '';
                }}
                style={{ maxWidth: '100%' }}
              >
                <option value="">Menüyü Görüntüle (masa seç)</option>
                {masalar.map((m) => <option key={m.id} value={m.ad}>{m.ad}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {bildirimIzniDurumu === 'reddedildi' && (
        <div className="hata-kutu" style={{ marginBottom: 16 }}>
          Bildirimler açılamadı: {bildirimHataMetni || 'Tarayıcı ayarlarından bu site için bildirimlere izin verip tekrar deneyin.'}
        </div>
      )}
      {bildirimIzniDurumu === 'desteklenmiyor' && (
        <div className="hata-kutu" style={{ marginBottom: 16 }}>
          Bu tarayıcı push bildirimlerini desteklemiyor (Android'de Chrome kullanmanızı öneririz).
        </div>
      )}

      {hata && <div className="hata-kutu">{hata}</div>}

      {sekmeler.length > 1 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20
        }}>
          {sekmeler.map((s, i) => {
            const renk = SEKME_RENKLERI[i % SEKME_RENKLERI.length];
            const secili = sekme === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSekme(s.id)}
                style={{
                  padding: '10px 6px', borderRadius: 10, cursor: 'pointer',
                  fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  transition: 'background .15s, color .15s, box-shadow .15s',
                  border: `2px solid ${renk}`,
                  color: secili ? '#fff' : renk,
                  background: secili ? renk : '#fff',
                  boxShadow: secili ? `0 2px 6px ${renk}66` : 'none'
                }}
              >
                {s.ad}
              </button>
            );
          })}
        </div>
      )}

      {sekme === 'ISLETME_BILGILERI' && rol !== 'GARSON' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>İşletme Bilgileri</h3>
          <p style={{ fontSize: 13, color: '#6b7684', marginTop: 0 }}>
            Bu bilgiler, menü sayfasının en altında müşterilere gösterilen footer'da yer alır.
          </p>
          <form onSubmit={isletmeBilgileriKaydet} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>İşletmenin Adı</label>
              <input value={isletmeBilgiAd} onChange={(e) => setIsletmeBilgiAd(e.target.value)} required style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Adresi</label>
              <input value={isletmeBilgiAdres} onChange={(e) => setIsletmeBilgiAdres(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Telefonu</label>
              <input value={isletmeBilgiTelefon} onChange={(e) => setIsletmeBilgiTelefon(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Ek Bilgi (web sitesi, e-posta vb.)</label>
              <input
                value={isletmeBilgiEkBilgi}
                onChange={(e) => setIsletmeBilgiEkBilgi(e.target.value)}
                placeholder="Örn: www.hastane.tv - info@hastane.tv"
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#1e2a3a', display: 'block', marginBottom: 8 }}>Çalışma Saatleri</label>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Hafta İçi</label>
                  <input value={calismaSaatHaftaIci} onChange={(e) => setCalismaSaatHaftaIci(e.target.value)} placeholder="Örn: 09:00 - 22:00" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Cumartesi</label>
                  <input value={calismaSaatCumartesi} onChange={(e) => setCalismaSaatCumartesi(e.target.value)} placeholder="Örn: 09:00 - 23:00" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Pazar</label>
                  <input value={calismaSaatPazar} onChange={(e) => setCalismaSaatPazar(e.target.value)} placeholder="Örn: Kapalı" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>
            </div>

            <button type="submit" disabled={isletmeBilgiKaydediliyor} className="btn btn-vurgu" style={{ alignSelf: 'flex-start' }}>
              {isletmeBilgiKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </form>
          {isletmeBilgiMesaj && (
            <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: isletmeBilgiMesaj.tip === 'basari' ? '#1e5c30' : '#8b1a1a' }}>
              {isletmeBilgiMesaj.metin}
            </div>
          )}
        </div>
      )}

      {sekme === 'MENU' && rol !== 'GARSON' && (
        <>
          <div className="kart" style={{ marginBottom: 20 }}>
            <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Google Yorum İste</h3>
            <p style={{ fontSize: 13, color: '#6b7684', marginTop: 0 }}>
              Google işletme profilinizin yorum linkini girin — menü sayfasında banner'ın altında "Google Yorumu Yaz"
              butonu belirir ve müşteri tıklayınca bu link yeni sekmede açılır. Boş bırakılırsa buton hiç görünmez.
            </p>
            <form onSubmit={googleYorumLinkiKaydet} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Yorum Linki</label>
                <input
                  value={googleYorumLink}
                  onChange={(e) => setGoogleYorumLink(e.target.value)}
                  placeholder="https://g.page/r/XXXXXXXXXX/review"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <button type="submit" disabled={googleYorumKaydediliyor} className="btn btn-vurgu">
                {googleYorumKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </form>
            {googleYorumMesaj && (
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: googleYorumMesaj.tip === 'basari' ? '#1e5c30' : '#8b1a1a' }}>
                {googleYorumMesaj.metin}
              </div>
            )}
          </div>

          <div className="kart" style={{ marginBottom: 20 }}>
            <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Görünüm Ayarları</h3>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>
                  Banner (menü sayfasının en üstündeki geniş görsel)
                </label>
                {bannerUrl && (
                  <img src={dosyaUrl(bannerUrl)} alt="Banner önizleme" style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />
                )}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={bannerSecildi} />
                {bannerYukleniyor && <div style={{ fontSize: 12, color: '#6b7684', marginTop: 4 }}>Yükleniyor...</div>}
              </div>
              <div style={{ width: 160 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Logo</label>
                {logoUrl && (
                  <img src={dosyaUrl(logoUrl)} alt="Logo önizleme" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />
                )}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={logoSecildi} />
                {logoYukleniyor && <div style={{ fontSize: 12, color: '#6b7684', marginTop: 4 }}>Yükleniyor...</div>}
              </div>
            </div>
          </div>

          <div className="kart" style={{ marginBottom: 20 }}>
            <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Tema Seçimi</h3>
            <p style={{ fontSize: 13, color: '#6b7684', marginTop: 0 }}>
              Müşterinin gördüğü menü sayfasının tüm görünümü (renkler, butonlar, arka plan) seçtiğiniz temaya göre değişir.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {TEMALAR.map((t) => (
                <button
                  key={t.id}
                  onClick={() => temaSec(t.id)}
                  disabled={temaKaydediliyor}
                  style={{
                    width: 120, border: tema === t.id ? `3px solid ${t.accent}` : '1px solid #e0e0e0',
                    borderRadius: 12, padding: 10, cursor: 'pointer', background: '#fff', textAlign: 'center'
                  }}
                >
                  <div style={{
                    height: 50, borderRadius: 8, marginBottom: 8,
                    background: `linear-gradient(135deg, ${t.koyu}, ${t.bgUst})`,
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 6
                  }}>
                    <div style={{ width: '70%', height: 10, borderRadius: 5, background: t.accent }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1e2a3a' }}>{t.ad}</div>
                  {tema === t.id && <div style={{ fontSize: 11, color: t.accent, fontWeight: 700, marginTop: 2 }}>✓ Seçili</div>}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, color: '#1e2a3a' }}>Ürün Grupları</h3>
            <button onClick={() => grupModaliAc(null)} className="btn btn-vurgu">+ Yeni Ürün Grubu</button>
          </div>

          {yukleniyor ? (
            <div>Yükleniyor...</div>
          ) : gruplar.length === 0 ? (
            <div className="kart" style={{ color: '#6b7684', marginBottom: 24 }}>Henüz ürün grubu eklenmedi.</div>
          ) : (
            gruplar.map((grup) => (
              <div key={grup.id} className="kart" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, color: '#e8600c' }}>{grup.ad}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => urunModaliAc(null, grup.id)} className="btn btn-vurgu" style={{ padding: '6px 12px', fontSize: 12 }}>+ Ürün Ekle</button>
                    <button onClick={() => grupModaliAc(grup)} className="btn btn-koyu" style={{ padding: '6px 12px', fontSize: 12 }}>Düzenle</button>
                    <button onClick={() => grupSilOnayla(grup)} className="btn btn-kirmizi" style={{ padding: '6px 12px', fontSize: 12 }}>Sil</button>
                  </div>
                </div>

                {urunler.filter((u) => u.grupId === grup.id).length === 0 ? (
                  <div style={{ color: '#6b7684', fontSize: 13 }}>Bu grupta henüz ürün yok.</div>
                ) : (
                  urunler.filter((u) => u.grupId === grup.id).map((urun) => (
                    <div key={urun.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                      {urun.gorselUrl && <img src={dosyaUrl(urun.gorselUrl)} alt={urun.ad} style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover' }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{urun.ad} — {paraFormatla(urun.fiyat)} ₺</div>
                        {urun.aciklama && <div style={{ fontSize: 12, color: '#6b7684' }}>{urun.aciklama}</div>}
                      </div>
                      <button onClick={() => urunModaliAc(urun)} className="btn btn-koyu" style={{ padding: '5px 10px', fontSize: 12 }}>Düzenle</button>
                      <button onClick={() => urunSilOnayla(urun)} className="btn btn-kirmizi" style={{ padding: '5px 10px', fontSize: 12 }}>Sil</button>
                    </div>
                  ))
                )}
              </div>
            ))
          )}
        </>
      )}

      {sekme === 'WIFI' && rol !== 'GARSON' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Sipariş Ağı (IP) Ayarları</h3>
          <p style={{ fontSize: 13, color: '#6b7684', marginTop: 0 }}>
            Müşterilerin sipariş verebilmesi için, cafenizin WiFi ağına bağlıyken bu butona basmanız gerekir.
            Bu, siparişlerin yalnızca fiziksel olarak cafede bulunan müşterilerden gelmesini sağlar.
          </p>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            Kayıtlı ağ: <strong>{izinVerilenIp || 'Henüz tanımlanmadı'}</strong>
          </div>
          <button onClick={ipButonaBasildi} disabled={ipGuncelleniyor} className="btn btn-vurgu">
            {ipGuncelleniyor ? 'Kaydediliyor...' : "Şu Anki Ağı (Bu Cafe'nin WiFi'si) Kaydet"}
          </button>
          {ipMesaj && <div style={{ marginTop: 8, fontSize: 13, color: '#1e5c30', fontWeight: 600 }}>{ipMesaj}</div>}
        </div>
      )}

      {sekme === 'WIFI' && rol !== 'GARSON' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Ağ Dışı Müşteriye Yönlendirme</h3>
          <p style={{ fontSize: 13, color: '#6b7684', marginTop: 0 }}>
            Cafe WiFi'sine bağlı olmayan bir müşteri sipariş/garson çağırma denediğinde, buraya
            girdiğiniz SSID ve şifre müşteri ekranında gösterilerek bağlanması kolaylaştırılır.
            Bu bilgi yukarıdaki "izin verilen IP" kontrolünü değiştirmez, yalnızca yönlendirme amaçlıdır.
          </p>
          <form onSubmit={wifiBilgiKaydet} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>WiFi Ağ Adı (SSID)</label>
              <input value={wifiSsid} onChange={(e) => setWifiSsid(e.target.value)} placeholder="Örn: OguzCafe-Misafir" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>WiFi Şifresi</label>
              <input value={wifiSifre} onChange={(e) => setWifiSifre(e.target.value)} placeholder="Örn: kahve1234" />
            </div>
            <button type="submit" disabled={wifiBilgiKaydediliyor} className="btn btn-vurgu">
              {wifiBilgiKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </form>
          {wifiBilgiMesaj && <div style={{ marginTop: 8, fontSize: 13, color: '#1e5c30', fontWeight: 600 }}>{wifiBilgiMesaj}</div>}
        </div>
      )}

      {sekme === 'WIFI' && rol !== 'GARSON' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Müşteri Fonksiyonları</h3>
          <p style={{ fontSize: 13, color: '#6b7684', marginTop: 0 }}>
            Sipariş Verme, Garson Çağırma ve Hesap İsteme özelliklerini ayrı ayrı açıp kapatabilir,
            her biri için cafe WiFi'sine bağlı olma şartını zorunlu tutup tutmayacağınızı seçebilirsiniz.
          </p>

          <FonksiyonAyarBlok baslik="Sipariş Verme" deger={siparisAyar} onChange={setSiparisAyar} />
          <FonksiyonAyarBlok baslik="Garson Çağırma" deger={garsonCagirAyar} onChange={setGarsonCagirAyar} />
          <FonksiyonAyarBlok baslik="Hesap İsteme" deger={hesapIsteAyar} onChange={setHesapIsteAyar} />

          <button onClick={fonksiyonAyarlariKaydet} disabled={fonksiyonKaydediliyor} className="btn btn-vurgu" style={{ marginTop: 10 }}>
            {fonksiyonKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          {fonksiyonMesaj && <div style={{ marginTop: 8, fontSize: 13, color: '#1e5c30', fontWeight: 600 }}>{fonksiyonMesaj}</div>}
        </div>
      )}

      {sekme === 'PERSONEL' && rol !== 'GARSON' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Garson Hesapları</h3>
          <p style={{ fontSize: 13, color: '#6b7684', marginTop: 0 }}>
            Garsonlar bu kullanıcı adı/şifre ile giriş yaptığında yalnızca "Siparişler" sekmesini görür.
          </p>

          <form onSubmit={personelEkle} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
            {personelHata && <div className="hata-kutu" style={{ width: '100%' }}>{personelHata}</div>}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Kullanıcı Adı</label>
              <input value={yeniPersonelKullaniciAdi} onChange={(e) => setYeniPersonelKullaniciAdi(e.target.value)} required />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Şifre</label>
              <input value={yeniPersonelSifre} onChange={(e) => setYeniPersonelSifre(e.target.value)} required />
            </div>
            <button type="submit" disabled={personelKaydediliyor} className="btn btn-vurgu">
              {personelKaydediliyor ? 'Ekleniyor...' : '+ Garson Ekle'}
            </button>
          </form>

          {personelYukleniyor ? (
            <div style={{ color: '#6b7684' }}>Yükleniyor...</div>
          ) : personel.length === 0 ? (
            <div style={{ color: '#6b7684', fontSize: 13 }}>Henüz garson hesabı eklenmedi.</div>
          ) : (
            personel.map((g) => {
              const atanmisMasalar = garsonMasalari(g.id);
              const tumu = atanmisMasalar.length === 0;
              return (
                <div key={g.id} style={{ padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <div style={{ fontWeight: 600 }}>{g.kullaniciAdi}</div>
                      <div style={{ fontSize: 11, color: '#6b7684' }}>Şifre: {g.sifre}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: tumu ? '#e8f0fe' : '#fff3e0', color: tumu ? '#1e4a8b' : '#8b5a1a' }}>
                      {tumu ? 'Masa: Tümü' : `${atanmisMasalar.length} masa`}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: g.aktif ? '#1e5c30' : '#c0392b' }}>{g.aktif ? 'Aktif' : 'Pasif'}</span>
                    <button onClick={() => setMasaAtamaAcikId(masaAtamaAcikId === g.id ? null : g.id)} className="btn btn-koyu" style={{ padding: '5px 10px', fontSize: 12 }}>
                      Masalar
                    </button>
                    <button onClick={() => personelAktifTikla(g)} className="btn btn-koyu" style={{ padding: '5px 10px', fontSize: 12 }}>
                      {g.aktif ? 'Pasifleştir' : 'Aktifleştir'}
                    </button>
                    <button onClick={() => personelSilOnayla(g)} className="btn btn-kirmizi" style={{ padding: '5px 10px', fontSize: 12 }}>Sil</button>
                  </div>

                  {masaAtamaAcikId === g.id && (
                    <div style={{ marginTop: 10, padding: 12, background: '#fafafa', borderRadius: 8 }}>
                      <div style={{ fontSize: 12, color: '#6b7684', marginBottom: 8 }}>
                        Bu garsona bildirim gönderilecek masaları seçin. Hiç masa seçilmezse (varsayılan) <strong>Tümü</strong> kabul edilir —
                        kısıt koymak isteğe bağlıdır.
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={tumu}
                          disabled={masaAtamaKaydediliyor}
                          onChange={() => masaAtamasiKaydet(g.id, [])}
                        />
                        Tümü (kısıtsız)
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                        {masalar.map((m) => (
                          <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={atanmisMasalar.includes(m.id)}
                              disabled={masaAtamaKaydediliyor}
                              onChange={() => masaSecimiToggle(g.id, m.id)}
                            />
                            {m.ad}
                          </label>
                        ))}
                      </div>
                      {masalar.length === 0 && <div style={{ fontSize: 12, color: '#6b7684' }}>Henüz masa tanımlanmadı.</div>}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {sekme === 'MASALAR' && rol !== 'GARSON' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Masa Tanımları</h3>
          <p style={{ fontSize: 13, color: '#6b7684', marginTop: 0 }}>
            Burada tanımladığınız masalar, İstatistikler filtresindeki masa listesinde görünür ve her biri için QR kod oluşturabilirsiniz.
          </p>

          <form onSubmit={masaEkle} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 16 }}>
            {masaHata && <div className="hata-kutu" style={{ width: '100%' }}>{masaHata}</div>}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Masa Adı</label>
              <input value={yeniMasaAd} onChange={(e) => setYeniMasaAd(e.target.value)} placeholder="Örn: 1, Teras 3..." required />
            </div>
            <button type="submit" disabled={masaKaydediliyor} className="btn btn-vurgu">
              {masaKaydediliyor ? 'Ekleniyor...' : '+ Masa Ekle'}
            </button>
          </form>

          {masalarYukleniyor ? (
            <div style={{ color: '#6b7684' }}>Yükleniyor...</div>
          ) : masalar.length === 0 ? (
            <div style={{ color: '#6b7684', fontSize: 13 }}>Henüz masa tanımlanmadı.</div>
          ) : (
            masalar.map((m) => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                {masaDuzenlenenId === m.id ? (
                  <>
                    <input value={masaDuzenlenenAd} onChange={(e) => setMasaDuzenlenenAd(e.target.value)} style={{ flex: 1 }} />
                    <button onClick={() => masaDuzenlemeyiKaydet(m.id)} className="btn btn-vurgu" style={{ padding: '5px 10px', fontSize: 12 }}>Kaydet</button>
                    <button onClick={() => setMasaDuzenlenenId(null)} className="btn btn-koyu" style={{ padding: '5px 10px', fontSize: 12 }}>Vazgeç</button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1, fontWeight: 600 }}>{m.ad}</div>
                    <button onClick={() => masaQrOlustur(m)} className="btn btn-vurgu" style={{ padding: '5px 10px', fontSize: 12 }}>QR Oluştur</button>
                    <button onClick={() => masaDuzenlemeyeBasla(m)} className="btn btn-koyu" style={{ padding: '5px 10px', fontSize: 12 }}>Değiştir</button>
                    <button onClick={() => masaSilOnayla(m)} className="btn btn-kirmizi" style={{ padding: '5px 10px', fontSize: 12 }}>Sil</button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {sekme === 'SIPARISLER' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center' }}>
            <button
              onClick={() => setSiparisSekme('BEKLEYEN')}
              style={{
                padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                background: siparisSekme === 'BEKLEYEN' ? '#e8600c' : '#f0f0f0',
                color: siparisSekme === 'BEKLEYEN' ? '#fff' : '#1e2a3a'
              }}
            >
              Bekleyen Siparişler {bekleyenSiparisler.length > 0 && `(${bekleyenSiparisler.length})`}
            </button>
            <button
              onClick={() => setSiparisSekme('ESKI')}
              style={{
                padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                background: siparisSekme === 'ESKI' ? '#e8600c' : '#f0f0f0',
                color: siparisSekme === 'ESKI' ? '#fff' : '#1e2a3a'
              }}
            >
              Eski Siparişler
            </button>
          </div>

          {siparisYukleniyor ? (
            <div style={{ color: '#6b7684' }}>Yükleniyor...</div>
          ) : siparisSekme === 'BEKLEYEN' ? (
            bekleyenSiparisler.length === 0 ? (
              <div style={{ color: '#6b7684', fontSize: 13 }}>Bekleyen sipariş yok.</div>
            ) : (
              bekleyenSiparisler.map((g) => (
                <div key={g.id} style={{ padding: '10px 0', borderTop: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontWeight: 700, color: '#1e2a3a' }}>
                      {g.masaNo ? `Masa ${g.masaNo}` : 'Masa belirtilmemiş'}
                      <span style={{ fontWeight: 400, color: '#6b7684', fontSize: 12, marginLeft: 8 }}>
                        {new Date(g.tarih).toLocaleString('tr-TR')}
                      </span>
                      <span style={{
                        marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: g.durum === 'HAZIRLANIYOR' ? '#fff3e0' : '#e8f0fe',
                        color: g.durum === 'HAZIRLANIYOR' ? '#8b5a1a' : '#1e4a8b'
                      }}>
                        {g.durum === 'HAZIRLANIYOR' ? 'Hazırlanıyor' : 'Bekliyor'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {g.durum === 'BEKLIYOR' && (
                        <button onClick={() => setOnayPopup({ siparis: g, aksiyon: 'HAZIRLANIYOR' })} className="btn btn-vurgu" style={{ padding: '5px 12px', fontSize: 12 }}>Onayla</button>
                      )}
                      {g.durum === 'HAZIRLANIYOR' && (
                        <button onClick={() => setOnayPopup({ siparis: g, aksiyon: 'TESLIM_EDILDI' })} className="btn btn-vurgu" style={{ padding: '5px 12px', fontSize: 12 }}>Teslim Et</button>
                      )}
                      <button onClick={() => siparisiIptalEt(g.id)} className="btn btn-kirmizi" style={{ padding: '5px 12px', fontSize: 12 }}>İptal Et</button>
                    </div>
                  </div>
                  {g.urunler.map((k, i) => (
                    <div key={i} style={{ fontSize: 13, color: '#374151', paddingLeft: 4 }}>
                      {k.adet} x {k.urunAdi} — {paraFormatla(k.fiyat * k.adet)} ₺
                    </div>
                  ))}
                  {g.aciklama && (
                    <div style={{ fontSize: 12, color: '#8b5a1a', marginTop: 4, fontStyle: 'italic' }}>Not: {g.aciklama}</div>
                  )}
                  <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4, color: '#e8600c' }}>Toplam: {paraFormatla(g.toplam)} ₺</div>
                </div>
              ))
            )
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <p style={{ fontSize: 12, color: '#6b7684', margin: 0 }}>Son 24 saatteki teslim edilen / iptal edilen siparişler.</p>
                {eskiSiparisler.length > 0 && (
                  <button onClick={eskiListesiniTemizle} className="btn btn-koyu" style={{ padding: '5px 12px', fontSize: 12 }}>Listeyi Temizle</button>
                )}
              </div>
              {eskiSiparisler.length === 0 ? (
                <div style={{ color: '#6b7684', fontSize: 13 }}>Gösterilecek eski sipariş yok.</div>
              ) : (
                eskiSiparisler.map((g) => (
                  <div key={g.id} style={{ padding: '10px 0', borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontWeight: 700, color: '#1e2a3a' }}>
                        {g.masaNo ? `Masa ${g.masaNo}` : 'Masa belirtilmemiş'}
                        <span style={{ fontWeight: 400, color: '#6b7684', fontSize: 12, marginLeft: 8 }}>
                          {new Date(g.tarih).toLocaleString('tr-TR')}
                        </span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: g.durum === 'IPTAL' ? '#c0392b' : '#1e5c30' }}>
                        {g.durum === 'IPTAL' ? '✕ İptal Edildi' : '✓ Teslim Edildi'}
                      </span>
                    </div>
                    {g.urunler.map((k, i) => (
                      <div key={i} style={{ fontSize: 13, color: '#374151', paddingLeft: 4 }}>
                        {k.adet} x {k.urunAdi} — {paraFormatla(k.fiyat * k.adet)} ₺
                      </div>
                    ))}
                    {g.aciklama && (
                      <div style={{ fontSize: 12, color: '#8b5a1a', marginTop: 4, fontStyle: 'italic' }}>Not: {g.aciklama}</div>
                    )}
                    <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4, color: '#e8600c' }}>Toplam: {paraFormatla(g.toplam)} ₺</div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}

      {sekme === 'BILDIRIMLER' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Bildirimler</h3>
          {bildirimlerYukleniyor ? (
            <div style={{ color: '#6b7684' }}>Yükleniyor...</div>
          ) : bildirimler.length === 0 ? (
            <div style={{ color: '#6b7684', fontSize: 13 }}>Bekleyen bildirim yok.</div>
          ) : (
            bildirimler.map((b) => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: 20 }}>{b.tip === 'GARSON_CAGIR' ? '🔔' : '🧾'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#1e2a3a' }}>
                    {b.masaNo ? `Masa ${b.masaNo}` : 'Masa belirtilmemiş'} — {b.tip === 'GARSON_CAGIR' ? 'Garson Çağırıyor' : 'Hesap İstiyor'}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7684' }}>{new Date(b.tarih).toLocaleString('tr-TR')}</div>
                </div>
                {b.tip === 'HESAP_ISTE' && (
                  <button
                    onClick={() => { setAdisyonMasaNo(b.masaNo || ''); setSekme('ADISYON'); }}
                    className="btn btn-koyu" style={{ padding: '5px 12px', fontSize: 12 }}
                  >
                    Adisyona Git
                  </button>
                )}
                <button onClick={() => bildirimiOnayla(b.id)} className="btn btn-vurgu" style={{ padding: '5px 12px', fontSize: 12 }}>Onayla</button>
              </div>
            ))
          )}
        </div>
      )}

      {sekme === 'ADISYON' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Adisyon</h3>
          <p style={{ fontSize: 13, color: '#6b7684', marginTop: 0 }}>
            Bir masa seçip "Getir"e basın — o masanın henüz kapatılmamış, teslim edilmiş siparişleri listelenir.
            "Adisyonu Kapat" ile masa temizlenir; bir sonraki müşteri bu siparişleri görmez.
          </p>
          <form onSubmit={adisyonuGetir} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Masa</label>
              <select value={adisyonMasaNo} onChange={(e) => { setAdisyonMasaNo(e.target.value); setAdisyonKayitlar(null); }} required>
                <option value="">Seçiniz</option>
                {masalar.map((m) => <option key={m.id} value={m.ad}>{m.ad}</option>)}
              </select>
            </div>
            <button type="submit" disabled={adisyonYukleniyor || !adisyonMasaNo} className="btn btn-vurgu">
              {adisyonYukleniyor ? 'Getiriliyor...' : 'Getir'}
            </button>
          </form>

          {adisyonKayitlar !== null && (
            adisyonKayitlar.length === 0 ? (
              <div style={{ color: '#6b7684', fontSize: 13 }}>Bu masanın kapatılmamış bir adisyonu yok.</div>
            ) : (
              <>
                {adisyonKayitlar.map((g) => (
                  <div key={g.id} style={{ padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ fontSize: 12, color: '#6b7684' }}>{new Date(g.tarih).toLocaleString('tr-TR')}</div>
                    {g.urunler.map((k, i) => (
                      <div key={i} style={{ fontSize: 13, color: '#374151' }}>{k.adet} x {k.urunAdi} — {paraFormatla(k.fiyat * k.adet)} ₺</div>
                    ))}
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#e8600c' }}>Toplam: {paraFormatla(adisyonToplam)} ₺</div>
                  <button onClick={() => setAdisyonKapatPopup(true)} className="btn btn-vurgu">Adisyonu Kapat</button>
                </div>
              </>
            )
          )}
        </div>
      )}

      {sekme === 'ISTATISTIK' && rol !== 'GARSON' && (
        <div className="kart" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>İstatistikler</h3>
          <form onSubmit={istatistikFiltrele} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Başlangıç Tarihi</label>
              <input type="date" value={istBaslangic} onChange={(e) => setIstBaslangic(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Bitiş Tarihi</label>
              <input type="date" value={istBitis} onChange={(e) => setIstBitis(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Masa</label>
              <select value={istMasaNo} onChange={(e) => setIstMasaNo(e.target.value)}>
                <option value="">Tümü</option>
                {masalar.map((m) => <option key={m.id} value={m.ad}>{m.ad}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Ürün Grubu</label>
              <select value={istGrupId} onChange={(e) => { setIstGrupId(e.target.value); setIstUrunId(''); }}>
                <option value="">Tümü</option>
                {gruplar.map((g) => <option key={g.id} value={g.id}>{g.ad}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Ürün</label>
              <select value={istUrunId} onChange={(e) => setIstUrunId(e.target.value)}>
                <option value="">Tümü</option>
                {urunler
                  .filter((u) => !istGrupId || u.grupId === Number(istGrupId))
                  .map((u) => <option key={u.id} value={u.id}>{u.ad}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Durum</label>
              <select value={istDurum} onChange={(e) => setIstDurum(e.target.value)}>
                <option value="">Tümü</option>
                <option value="ONAYLI">Onaylı (iptal hariç)</option>
                <option value="IPTAL">Yalnızca İptal</option>
              </select>
            </div>
            <button type="submit" disabled={istYukleniyor} className="btn btn-vurgu">
              {istYukleniyor ? 'Aranıyor...' : 'Filtrele'}
            </button>
          </form>

          {istKayitlar === null ? (
            <div style={{ color: '#6b7684', fontSize: 13 }}>Filtreleri seçip "Filtrele"ye basın.</div>
          ) : istKayitlar.length === 0 ? (
            <div style={{ color: '#6b7684', fontSize: 13 }}>Bu filtrelere uyan sipariş bulunamadı.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="veri-tablosu" style={{ width: '100%', minWidth: 560 }}>
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Masa</th>
                      <th>Ürün</th>
                      <th>Adet</th>
                      <th>Tutar</th>
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {istKayitlar.map((k, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(k.tarih).toLocaleString('tr-TR')}</td>
                        <td>{k.masaNo || '-'}</td>
                        <td>{k.urunAdi}</td>
                        <td>{k.adet}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{paraFormatla(k.fiyat * k.adet)} ₺</td>
                        <td>{k.durum}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ textAlign: 'right', fontWeight: 800, fontSize: 16, color: '#e8600c', marginTop: 14 }}>
                Toplam: {paraFormatla(istToplam)} ₺
              </div>
            </>
          )}
        </div>
      )}

      {onayPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="kart" style={{ width: 360 }}>
            <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>
              {onayPopup.aksiyon === 'HAZIRLANIYOR' ? 'Siparişi Hazırlanıyora Al' : 'Siparişi Teslim Et'}
            </h3>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              {onayPopup.siparis.masaNo ? `Masa ${onayPopup.siparis.masaNo}` : 'Masa belirtilmemiş'}
            </div>
            {onayPopup.siparis.urunler.map((k, i) => (
              <div key={i} style={{ fontSize: 13, color: '#374151' }}>{k.adet} x {k.urunAdi}</div>
            ))}
            {onayPopup.siparis.aciklama && (
              <div style={{ fontSize: 12, color: '#8b5a1a', marginTop: 6, fontStyle: 'italic' }}>Not: {onayPopup.siparis.aciklama}</div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => setOnayPopup(null)} className="btn btn-koyu" style={{ flex: 1 }}>Vazgeç</button>
              <button onClick={onayPopupOnayla} className="btn btn-vurgu" style={{ flex: 1 }}>
                {onayPopup.aksiyon === 'HAZIRLANIYOR' ? 'Hazırlanıyor' : 'Teslim Edildi'}
              </button>
            </div>
          </div>
        </div>
      )}

      {adisyonKapatPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="kart" style={{ width: 360 }}>
            <h3 style={{ marginTop: 0, color: '#1e2a3a' }}>Adisyonu Kapat</h3>
            <p style={{ fontSize: 13, color: '#374151' }}>
              Masa {adisyonMasaNo} için toplam <strong>{paraFormatla(adisyonToplam)} ₺</strong> tahsil edildiğini onaylıyor musunuz?
              Onaylarsanız bu siparişler adisyondan kapanır, bir sonraki müşteri bunları görmez.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <button onClick={() => setAdisyonKapatPopup(false)} className="btn btn-koyu" style={{ flex: 1 }}>Vazgeç</button>
              <button onClick={adisyonuKapat} className="btn btn-vurgu" style={{ flex: 1 }}>Onayla, Kapat</button>
            </div>
          </div>
        </div>
      )}

      {grupModalAcik && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <form onSubmit={grupKaydet} className="kart" style={{ width: 380 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: '#1e2a3a' }}>{grupDuzenlenen ? 'Ürün Grubunu Düzenle' : 'Yeni Ürün Grubu'}</h3>
              <button type="button" onClick={() => setGrupModalAcik(false)} className="btn btn-koyu">✕</button>
            </div>
            {modalHata && <div className="hata-kutu">{modalHata}</div>}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Grup Adı</label>
              <input value={grupAd} onChange={(e) => setGrupAd(e.target.value)} required />
            </div>
            <button type="submit" disabled={kaydediliyor} className="btn btn-vurgu" style={{ width: '100%' }}>
              {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </form>
        </div>
      )}

      {urunModalAcik && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <form onSubmit={urunKaydet} className="kart" style={{ width: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: '#1e2a3a' }}>{urunDuzenlenen ? 'Ürünü Düzenle' : 'Yeni Ürün'}</h3>
              <button type="button" onClick={() => setUrunModalAcik(false)} className="btn btn-koyu">✕</button>
            </div>
            {modalHata && <div className="hata-kutu">{modalHata}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Ürün Grubu</label>
                <select value={urunGrupId} onChange={(e) => setUrunGrupId(e.target.value)} required>
                  <option value="">Seçiniz</option>
                  {gruplar.map((g) => <option key={g.id} value={g.id}>{g.ad}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Ürün Adı</label>
                <input value={urunAd} onChange={(e) => setUrunAd(e.target.value)} required />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Ürün Görseli</label>
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={gorselSecildi} />
                {gorselYukleniyor && <div style={{ fontSize: 12, color: '#6b7684', marginTop: 4 }}>Yükleniyor...</div>}
                {urunGorselUrl && !gorselYukleniyor && (
                  <img src={dosyaUrl(urunGorselUrl)} alt="Önizleme" style={{ width: 60, height: 60, borderRadius: 6, objectFit: 'cover', marginTop: 6 }} />
                )}
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Fiyat (₺)</label>
                <input type="number" step="0.01" value={urunFiyat} onChange={(e) => setUrunFiyat(e.target.value)} required />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7684', display: 'block', marginBottom: 6 }}>Açıklama</label>
                <textarea rows={3} value={urunAciklama} onChange={(e) => setUrunAciklama(e.target.value)} />
              </div>
              <button type="submit" disabled={kaydediliyor || gorselYukleniyor} className="btn btn-vurgu">
                {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Bir fonksiyon (Sipariş Verme / Garson Çağırma / Hesap İsteme) için Pasif / Aktif seçimi; Aktif
// seçilirse altında WiFi'ye bağlı olma şartının Zorunlu mu Serbest mi olacağını soran alt seçenek çıkar.
function FonksiyonAyarBlok({ baslik, deger, onChange }) {
  const aktif = deger !== 'PASIF';

  return (
    <div style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
      <div style={{ fontWeight: 700, color: '#1e2a3a', marginBottom: 8 }}>{baslik}</div>
      <div style={{ display: 'flex', gap: 18 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="radio" checked={aktif} onChange={() => onChange('ZORUNLU')} />
          Aktif
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="radio" checked={!aktif} onChange={() => onChange('PASIF')} />
          Pasif
        </label>
      </div>

      {aktif && (
        <div style={{ marginTop: 8, marginLeft: 20, paddingLeft: 12, borderLeft: '2px solid #f0f0f0' }}>
          <div style={{ fontSize: 12, color: '#6b7684', marginBottom: 6 }}>İşletmenin WiFi Ağına bağlı olmak:</div>
          <div style={{ display: 'flex', gap: 18 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={deger === 'ZORUNLU'} onChange={() => onChange('ZORUNLU')} />
              Zorunlu <span style={{ color: '#6b7684', fontWeight: 400 }}>(Güvenlik açısından önerilir)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={deger === 'SERBEST'} onChange={() => onChange('SERBEST')} />
              Zorunlu Değil
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
